import { Component, DestroyRef, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthSrcDirective, UiButtonComponent } from '@ui';
import { PersonService } from '../../../../../../services/person.service';
import { FormArray, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { PersonModel } from '@models/person.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith, take } from 'rxjs';
import { PersonGroupsModel } from '@models/person-groups.model';
import { PersonDialogService } from '../../../../../../services/person-dialog.service';
import { Store } from '@ngrx/store';
import { personFeature } from '@states/person/person.feature';
import { PersonActions } from '@states/person/person.actions';
import { Actions, ofType } from '@ngrx/effects';

@Component({
  selector: 'app-save-new-persons-dialog',
  imports: [
    TranslatePipe,
    UiButtonComponent,
    AuthSrcDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './save-new-persons-dialog.component.html',
  styleUrl: './save-new-persons-dialog.component.scss',
})
export class SaveNewPersonsDialogComponent {
  private readonly personService = inject(PersonService);
  private readonly personDialogService = inject(PersonDialogService);
  private readonly dialogRef = inject(MatDialogRef<SaveNewPersonsDialogComponent>);
  private readonly store$ = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly persons = inject<PersonModel.UnsavedPerson[]>(MAT_DIALOG_DATA);
  protected readonly personNameForms = new FormArray<FormControl<string>>(
    this.persons.map((person, index) => new FormControl<string>(PersonModel.getDefaultPersonName(index + 1), {
      nonNullable: true,
      validators: [Validators.required],
    })),
  );

  protected readonly arePersonNamesValid = toSignal(
    this.personNameForms.valueChanges.pipe(
      startWith(this.personNameForms.getRawValue()),
      map(() => this.personNameForms.controls.every((control) => !!control.value.trim())),
    ),
    { initialValue: false },
  );
  protected readonly isBulkCreatingPendingPersons = this.store$.selectSignal(
    personFeature.selectIsBulkCreatingPendingPersons,
  );

  protected getImagePath(person: PersonModel.UnsavedPerson, gcp: boolean = true): string {
    return this.personService.getPersonImagePath(person.bestImage, false, gcp);
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  protected openAddPersonToGroupsDialog(): void {
    if (!this.arePersonNamesValid()) {
      return;
    }

    const data: PersonGroupsModel.AddPersonToGroupsDialogData = {
      personType: PersonGroupsModel.AddPersonToGroupDialogTab.UnsavedPersons,
      personIds: [],
      unsavedPersonDtos: this.persons.map((person, i) => ({
        bucketId: person.bucketId,
        name: this.personNameForms.controls[i].getRawValue().trim(),
      })),
    };

    this.personDialogService
      .openAddPersonToGroups(data)
      .afterClosed()
      .pipe(
        filter(Boolean),
        take(1),
      )
      .subscribe(() => this.closeDialog());
  }

  protected savePersons(): void {
    if (!this.arePersonNamesValid() || this.isBulkCreatingPendingPersons()) {
      return;
    }

    const persons = this.persons.map((person, index) => ({
      bucketId: person.bucketId,
      name: this.personNameForms.controls[index].getRawValue().trim(),
    }));

    this.closeDialogOnBulkCreateSuccess(persons.map((person) => person.bucketId));
    this.store$.dispatch(PersonActions.bulkCreatePendingPersons({ persons }));
  }

  private closeDialogOnBulkCreateSuccess(bucketIds: number[]): void {
    this.actions$
      .pipe(
        ofType(PersonActions.bulkCreatePendingPersonsSuccess),
        filter((action) => this.sameBucketIds(action.bucketIds, bucketIds)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.closeDialog());
  }

  private sameBucketIds(left: number[], right: number[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    const sortedLeft = [...left].sort((a, b) => a - b);
    const sortedRight = [...right].sort((a, b) => a - b);

    return sortedLeft.every((id, index) => id === sortedRight[index]);
  }
}
